import { useState, useEffect } from 'react';
import { ContentImageField } from '@/components/admin/content-image-field';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { supabase } from '@/lib/supabase';
import { Search } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card, CardContent } from '@/components/ui/card';

export type ContentItemDeprecated = {
  id?: string;
  title: string;
  type: 'meditation' | 'music' | 'nature' | 'binaural' | 'video' | 'article';
  icon: any;
  imageUrl: string;
  videoUrl?: string;
  description?: string;
  content?: string;
};

interface EditContentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contentItem: ContentItemDeprecated | null;
  onSave: (updatedItem: ContentItemDeprecated) => void;
}

export function EditContentDialog({
  open,
  onOpenChange,
  contentItem,
  onSave = () => {},
}: EditContentDialogProps) {
  const [formData, setFormData] = useState<Partial<ContentItemDeprecated>>(
    contentItem || {},
  );
  const [isSaving, setIsSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<ContentItemDeprecated[]>(
    [],
  );
  const [isSearching, setIsSearching] = useState(false);

  // Reset form when content item changes or when creating new content
  useEffect(() => {
    if (contentItem) {
      setFormData(contentItem);
    } else {
      // Default values for new content
      setFormData({
        title: '',
        type: 'meditation',
        imageUrl: '',
        description: '',
      });
    }
  }, [contentItem]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value: string, name: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Reset search results when content type changes
    setSearchResults([]);
    setSearchTerm('');
  };

  const handleSearch = async () => {
    if (!searchTerm.trim() || !formData.type) return;

    setIsSearching(true);
    try {
      // Query Supabase for content items matching the search term and type
      const { data, error } = await supabase
        .from('content')
        .select('*')
        .eq('content_type', formData.type)
        .ilike('title', `%${searchTerm}%`)
        .limit(10);

      if (error) throw error;

      // Convert Supabase data to ContentItem format
      const formattedResults = data.map((item) => ({
        id: item.id,
        title: item.title,
        type: item.content_type,
        imageUrl: item.image_url || '',
        description: item.description || '',
        videoUrl: item.audio_url, // Using audio_url for video URL as well
        content: item.transcription || '', // Using transcription field for article content
      })) as ContentItemDeprecated[];

      setSearchResults(formattedResults);
    } catch (error) {
      console.error('Fout bij zoeken content:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const selectContentItem = (item: ContentItemDeprecated) => {
    setFormData(item);
    setSearchResults([]);
    setSearchTerm('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      // In a future implementation, this will save to Supabase
      // For now, just call the onSave callback with the updated item
      onSave(formData as ContentItemDeprecated);
      onOpenChange(false);
    } catch (error) {
      console.error('Fout bij opslaan content:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Allow dialog to open for new content creation (when contentItem is null)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {contentItem ? 'Content Bewerken' : 'Nieuwe Content Toevoegen'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Titel</Label>
            <Input
              id="title"
              name="title"
              value={formData.title || ''}
              onChange={handleChange}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="type">Type</Label>
            <Select
              value={formData.type}
              onValueChange={(value) => handleSelectChange(value, 'type')}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecteer type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="meditation">Meditatie</SelectItem>
                <SelectItem value="music">Muziek</SelectItem>
                <SelectItem value="nature">Natuur</SelectItem>
                <SelectItem value="binaural">Binauraal</SelectItem>
                <SelectItem value="tips">Tips</SelectItem>
                <SelectItem value="video">Video</SelectItem>
                <SelectItem value="article">Artikel</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {formData.type && (
            <div className="space-y-2">
              <Label htmlFor="searchContent">Zoek Bestaande Content</Label>
              <div className="flex gap-2">
                <Input
                  id="searchContent"
                  placeholder={`Zoek ${formData.type} content...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="flex-1"
                />
                <Button
                  type="button"
                  onClick={handleSearch}
                  disabled={isSearching}
                  size="icon"
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Search className="h-4 w-4" />
                </Button>
              </div>

              {searchResults.length > 0 && (
                <ScrollArea className="h-48 w-full rounded-md border">
                  <div className="p-4 space-y-2">
                    {searchResults.map((item) => (
                      <Card
                        key={item.id}
                        className="cursor-pointer hover:bg-muted transition-colors"
                        onClick={() => selectContentItem(item)}
                      >
                        <CardContent className="p-3 flex items-center gap-3">
                          {item.imageUrl && (
                            <div className="h-10 w-10 rounded overflow-hidden flex-shrink-0">
                              <img
                                src={item.imageUrl}
                                alt={item.title}
                                className="h-full w-full object-cover"
                              />
                            </div>
                          )}
                          <div>
                            <p className="font-medium">{item.title}</p>
                            {item.description && (
                              <p className="text-xs text-muted-foreground truncate max-w-[250px]">
                                {item.description}
                              </p>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </ScrollArea>
              )}

              {isSearching && (
                <p className="text-sm text-muted-foreground">Zoeken...</p>
              )}
              {!isSearching && searchTerm && searchResults.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  Geen resultaten gevonden
                </p>
              )}
            </div>
          )}

          <ContentImageField
            value={formData.imageUrl || ''}
            onChange={(url) =>
              setFormData((prev) => ({ ...prev, imageUrl: url }))
            }
            label="Afbeelding"
            required
          />

          <div className="space-y-2">
            <Label htmlFor="description">Beschrijving</Label>
            <Textarea
              id="description"
              name="description"
              value={formData.description || ''}
              onChange={handleChange}
              rows={3}
            />
          </div>

          {formData.type === 'video' && (
            <div className="space-y-2">
              <Label htmlFor="videoUrl">Video URL</Label>
              <Input
                id="videoUrl"
                name="videoUrl"
                value={formData.videoUrl || ''}
                onChange={handleChange}
                placeholder="https://example.com/video.mp4"
              />
            </div>
          )}

          {formData.type === 'article' && (
            <div className="space-y-2">
              <Label htmlFor="content">Artikel Inhoud</Label>
              <Textarea
                id="content"
                name="content"
                value={formData.content || ''}
                onChange={handleChange}
                rows={6}
                placeholder="<p>Voer hier de HTML inhoud van het artikel in...</p>"
              />
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="border-green-600 text-green-600 hover:bg-green-50"
            >
              Annuleren
            </Button>
            <Button
              type="submit"
              disabled={isSaving}
              className="bg-green-600 hover:bg-green-700"
            >
              {isSaving
                ? 'Opslaan...'
                : contentItem
                  ? 'Wijzigingen Opslaan'
                  : 'Content Toevoegen'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
